import { useEffect, useMemo, useState } from 'react';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { Sheet, fmtTime, fmtDateShort } from '../design/atoms';

/* "Your weekly times" — the checkout step for an offering with fixed weekly
   slots, after the coach step. The grid shows only times the chosen coach is
   free on EVERY week of what the member is buying; a time that clashes on
   some weeks is shown, muted, with the dates it clashes on, so the member
   picks with confidence. The same sheet moves an existing slot. */

export interface WeeklyTime { weekday: number; startTime: string }
export interface SlotClash { date: string; reason: 'off' | 'leave' | 'busy' | 'room' | 'member' }
export interface SlotGrid {
  scheduling: 'fixed_recurring' | 'member_choice';
  slots: { min: number; max: number };
  durationMin: number;
  trainer: { id: string; name: string; photoUrl: string | null };
  bookable: { id: string; name: string };
  branches: Array<{ id: string; name: string }>;
  branchId: string;
  window: { from: string; to: string; capped: boolean; weeks: number };
  today: string;
  days: Array<{ weekday: number; dates: number; times: Array<{ startTime: string; clashes: SlotClash[] }> }>;
  current: WeeklyTime[];
}
export interface SlotChoice { times: WeeklyTime[]; branchId: string; trainerName: string }

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Monday-first, the way people plan a training week.
const ORDER = [1, 2, 3, 4, 5, 6, 0];
export const timeLabel = (t: WeeklyTime) => `${WEEKDAYS[t.weekday]} ${fmtTime(t.startTime)}`;
const REASON: Record<SlotClash['reason'], string> = { off: 'not working', leave: 'on leave', busy: 'booked', room: 'no room', member: 'you’re booked' };

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-10-12" → "12 Oct" (the weekday is the column's). */
export const dayMonth = (ymd: string) => `${Number(ymd.slice(8, 10))} ${MON[Number(ymd.slice(5, 7)) - 1]}`;

export function clashText(clashes: SlotClash[]): string {
  const shown = clashes.slice(0, 2).map(c => dayMonth(c.date)).join(', ');
  return clashes.length > 2 ? `${shown} +${clashes.length - 2}` : shown;
}

export default function WeeklySlots({ token, url, title, count, initial, problem, busy, onClose, onChoose, onSkip }: {
  token: string;
  /** The grid endpoint (store offering or an existing slot). */
  url: string;
  title?: string;
  /** Override how many times to pick (moving one slot = 1). */
  count?: { min: number; max: number };
  initial?: WeeklyTime[];
  onClose: () => void;
  onChoose: (choice: SlotChoice) => void;
  /** Member's choice offerings: book when needed instead. */
  onSkip?: () => void;
  /** A refusal from saving the choice (e.g. SLOT_CONFLICT), shown in the sheet. */
  problem?: string | null;
  busy?: boolean;
}) {
  const [branchId, setBranchId] = useState<string | null>(null);
  const [grid, setGrid] = useState<SlotGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [day, setDay] = useState<number | null>(null);
  const [picked, setPicked] = useState<WeeklyTime[]>(initial ?? []);
  const [peek, setPeek] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setGrid(null);
    setError(null);
    const sep = url.includes('?') ? '&' : '?';
    mfetch(token, `${url}${branchId ? `${sep}branchId=${branchId}` : ''}`)
      .then(r => {
        if (!live) return;
        const g: SlotGrid = r.data;
        setGrid(g);
        // Start on the first day with a free time; a renewal starts from the current times.
        if (!initial?.length && g.current.length && picked.length === 0) {
          const free = g.current.filter(c => g.days.find(d => d.weekday === c.weekday)?.times.some(t => t.startTime === c.startTime && t.clashes.length === 0));
          setPicked(free.slice(0, (count ?? g.slots).max));
        }
        const firstFree = ORDER.find(w => g.days.find(d => d.weekday === w)?.times.some(t => t.clashes.length === 0));
        setDay(prev => prev ?? firstFree ?? ORDER.find(w => g.days.some(d => d.weekday === w)) ?? 1);
      })
      .catch(e => { if (live) setError(e instanceof MemberApiError ? e.message : 'Could not load the times'); });
    return () => { live = false; };
  }, [token, url, branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const need = count ?? grid?.slots ?? { min: 1, max: 1 };
  const today = grid?.days.find(d => d.weekday === day);
  const freeCount = (w: number) => grid?.days.find(d => d.weekday === w)?.times.filter(t => t.clashes.length === 0).length ?? 0;
  const isPicked = (t: WeeklyTime) => picked.some(p => p.weekday === t.weekday && p.startTime === t.startTime);
  const toggle = (t: WeeklyTime) => {
    setPeek(null);
    if (isPicked(t)) return setPicked(p => p.filter(x => !(x.weekday === t.weekday && x.startTime === t.startTime)));
    // Picking past the limit replaces the oldest pick — never a dead tap.
    setPicked(p => [...(p.length >= need.max ? p.slice(1) : p), t].sort((a, b) => ORDER.indexOf(a.weekday) - ORDER.indexOf(b.weekday) || a.startTime.localeCompare(b.startTime)));
  };
  const ready = picked.length >= need.min && picked.length <= need.max;
  const countLabel = need.min === need.max ? `Choose ${need.min}` : `Choose ${need.min}–${need.max}`;

  const summary = useMemo(() => {
    if (!grid) return ' ';
    const weeks = grid.window.capped ? `from ${fmtDateShort(grid.window.from)}` : `${grid.window.weeks} week${grid.window.weeks === 1 ? '' : 's'} from ${fmtDateShort(grid.window.from)}`;
    return `with ${grid.trainer.name.split(' ')[0]} · ${grid.durationMin} min · ${weeks}`;
  }, [grid]);

  return (
    <Sheet onClose={onClose}>
      <div className="col" style={{ gap: 2 }}>
        <div className="dp dp-m">{title ?? 'Your weekly times'}</div>
        <div className="sub trunc">{summary}</div>
      </div>

      {error && <div className="card flat" style={{ padding: 12 }}><div className="sub wrap">{error}</div></div>}
      {!grid && !error && <div className="skel" style={{ height: 320, borderRadius: 'var(--r-m)' }} />}

      {grid && (
        <>
          {grid.branches.length > 1 && (
            <div className="hscroll">
              {grid.branches.map(b => (
                <button key={b.id} className={`chip${b.id === grid.branchId ? ' on' : ''}`} onClick={() => { setBranchId(b.id); setPicked([]); }}>{b.name}</button>
              ))}
            </div>
          )}

          <div className="between" style={{ gap: 8 }}>
            <span className="lbl">{countLabel} · {picked.length} chosen</span>
            <span className="tiny nw">Free every week</span>
          </div>

          <div className="slot-days" role="tablist">
            {ORDER.map(w => {
              const has = grid.days.some(d => d.weekday === w && d.times.length > 0);
              const n = freeCount(w);
              const chosen = picked.filter(p => p.weekday === w).length;
              return (
                <button key={w} role="tab" aria-selected={day === w} disabled={!has} className={`slot-day${day === w ? ' on' : ''}${chosen ? ' has' : ''}`} onClick={() => { setDay(w); setPeek(null); }}>
                  <span>{WEEKDAYS[w]}</span>
                  <b className="num">{has ? n : '–'}</b>
                </button>
              );
            })}
          </div>

          {today && today.times.length > 0 ? (
            <div className="slot-grid">
              {today.times.map(t => {
                const slot = { weekday: today.weekday, startTime: t.startTime };
                const clash = t.clashes.length > 0;
                const on = isPicked(slot);
                return (
                  <button
                    key={t.startTime}
                    className={`slot-time${on ? ' on' : ''}${clash ? ' clash' : ''}`}
                    aria-disabled={clash}
                    onClick={() => (clash ? setPeek(peek === t.startTime ? null : t.startTime) : toggle(slot))}
                  >
                    <span className="num">{fmtTime(t.startTime)}</span>
                    {clash && <small>{clashText(t.clashes)}</small>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="card flat" style={{ padding: 14 }}>
              <div className="sub wrap">{grid.trainer.name.split(' ')[0]} has no time free every week on {WEEKDAYS[day ?? 1]}s — try another day.</div>
            </div>
          )}

          {peek && today && (() => {
            const t = today.times.find(x => x.startTime === peek);
            if (!t) return null;
            return (
              <div className="card flat" style={{ padding: 12 }}>
                <div className="sub wrap">
                  {WEEKDAYS[today.weekday]} {fmtTime(t.startTime)} clashes on {t.clashes.length} of {today.dates} weeks:{' '}
                  {t.clashes.slice(0, 6).map(c => `${dayMonth(c.date)} (${REASON[c.reason]})`).join(', ')}{t.clashes.length > 6 ? '…' : ''}.
                </div>
              </div>
            );
          })()}

          {picked.length > 0 && (
            <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
              {picked.map(p => (
                <button key={`${p.weekday}${p.startTime}`} className="chip on" onClick={() => toggle(p)} aria-label={`Remove ${timeLabel(p)}`}>
                  {timeLabel(p)} <Icon n="x" s={13} />
                </button>
              ))}
            </div>
          )}

          {problem && <div className="card flat" style={{ padding: 12 }}><div className="sub wrap">{problem}</div></div>}

          <button className="btn gold block" disabled={!ready || busy} onClick={() => onChoose({ times: picked, branchId: grid.branchId, trainerName: grid.trainer.name })}>
            {ready ? <>Hold {picked.length === 1 ? 'this time' : 'these times'} every week <Icon n="right" s={17} /></> : countLabel}
          </button>
          {onSkip && grid.scheduling === 'member_choice' && (
            <button className="btn ghost block" onClick={onSkip}>Skip — I’ll book as I go</button>
          )}
        </>
      )}
    </Sheet>
  );
}
